import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import commonEssence from '@sorare/core/src/assets/rewards/common-essence.png';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Snackbar } from '@sorare/core/src/atoms/snackbar/Snackbar';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { DropCardsWarningDialog } from '@sorare/core/src/components/commonGame/DropCardsWarningDialog';
import { SelectionSummary } from '@sorare/core/src/components/layout/SelectionSummary';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { useDropAndAddCards } from '@sorare/marketplace/src/components/commonCard/CommonPaymentBox/useDropAndAddCards';

import type {
  CardsDropSummaryQuery,
  CardsDropSummaryQueryVariables,
} from './__generated__/index.graphql';

type Props = {
  rarity: Rarity;
  onCancel: () => void;
  onDropped: (droppedCardSlugs: string[]) => void;
  selectedCardsSlugs: Nullable<string[]>;
};

const CARDS_DROP_SUMMARY_QUERY = gql`
  query CardsDropSummaryQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      signedDropCardPrice {
        price
        signedPrice
      }
      openedSo5Lineups {
        id
      }
      liveSo5Lineup {
        id
      }
      ...DropCardsWarningDialog_anyCardInterface
    }
  }
  ${DropCardsWarningDialog.fragments.anyCardInterface}
` as TypedDocumentNode<CardsDropSummaryQuery, CardsDropSummaryQueryVariables>;

export const CardsDropSummary = ({
  rarity,
  onCancel,
  onDropped,
  selectedCardsSlugs,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const maxCardsPerBatch = 100;
  const hasReachedMaxCardsPerBatch = Boolean(
    selectedCardsSlugs?.length && selectedCardsSlugs.length > maxCardsPerBatch
  );

  const { data, loading } = useQuery(CARDS_DROP_SUMMARY_QUERY, {
    variables: {
      slugs: selectedCardsSlugs || [],
    },
    skip: !selectedCardsSlugs?.length || hasReachedMaxCardsPerBatch,
  });
  const selectedCards =
    data?.anyCards?.filter(card => selectedCardsSlugs?.includes(card.slug)) ||
    [];
  const totalPoints = selectedCards.reduce(
    (acc, card) => acc + (card.signedDropCardPrice?.price || 0),
    0
  );
  const [displayedTotalPoints, setDisplayedTotalPoints] = useState(totalPoints);

  if (displayedTotalPoints !== totalPoints && !loading) {
    setDisplayedTotalPoints(totalPoints);
  }

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const { dropAndAddCards, loading: mutationLoading } = useDropAndAddCards();

  const hasWarnings = selectedCards.some(card => {
    return card.openedSo5Lineups.length > 0 || !!card.liveSo5Lineup;
  });

  const dropCards = async () => {
    const result = await dropAndAddCards(
      selectedCards
        .filter(card => !!card.signedDropCardPrice)
        .map(card => ({
          cardSlug: card.slug,
          signedPrice: card.signedDropCardPrice!.signedPrice,
        })),
      []
    );
    const droppedCards = result.data?.dropAndAddCards?.droppedCards;
    if (droppedCards?.length) {
      setSnackbarMessage(
        formatMessage(
          {
            id: 'CardsDropSummary.scrappedConfirmation',
            defaultMessage:
              'You gained {count, plural, one {1 Common Essence} other {{count} Common Essence}}',
          },
          {
            count: totalPoints,
          }
        )
      );
      onDropped?.(droppedCards.map(card => card.slug));
    }
  };

  return (
    <>
      <SelectionSummary
        nbCardsSelected={selectedCardsSlugs?.length || 0}
        color="rgba(var(--c-rgb-common), 1)"
        onCancel={onCancel}
        button={
          <LoadingButton
            loading={mutationLoading}
            startIcon={<DropCards />}
            size="medium"
            color="secondary"
            onClick={() => {
              if (hasWarnings) {
                setShowWarningDialog(true);
              } else {
                dropCards();
              }
            }}
            disabled={!selectedCardsSlugs?.length || hasReachedMaxCardsPerBatch}
          >
            <FormattedMessage {...fantasy.scrap} />
          </LoadingButton>
        }
      >
        <Vertical>
          <Horizontal>
            <Horizontal gap={0.5}>
              <ScarcityIcon rarity={rarity} />
              <LabelL bold>
                <FormattedNumber value={(selectedCardsSlugs || []).length} />
              </LabelL>
            </Horizontal>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="4"
              height="4"
              viewBox="0 0 4 4"
              fill="none"
            >
              <circle cx="2" cy="2" r="2" fill="white" fillOpacity="0.17" />
            </svg>
            <Horizontal gap={0.5}>
              <img src={commonEssence} alt="" height={16} />
              <LabelL bold>
                <FormattedNumber value={currentUser?.commonCardPoints || 0} />
              </LabelL>
              {!!displayedTotalPoints && (
                <LabelL color="var(--c-onBase-success)">
                  (+
                  <AnimatedNumber value={displayedTotalPoints} />)
                </LabelL>
              )}
            </Horizontal>
          </Horizontal>
          {!!hasReachedMaxCardsPerBatch && (
            <LabelS color="var(--c-red-300)">
              <FormattedMessage
                id="CardsDropSummary.selectedCards.overLimit"
                defaultMessage="You can only scrap {count} cards at a time"
                values={{ count: maxCardsPerBatch }}
              />
            </LabelS>
          )}
        </Vertical>
      </SelectionSummary>
      <Snackbar
        open={!!snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        autoHideDuration={10000}
      />
      <DropCardsWarningDialog
        open={showWarningDialog}
        onCancel={() => setShowWarningDialog(false)}
        loading={mutationLoading}
        onContinue={async () => {
          try {
            await dropCards();
          } finally {
            setShowWarningDialog(false);
          }
        }}
        cards={selectedCards}
      />
    </>
  );
};
